import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import I18n from 'i18n-js';
import CreditCardOption from './credit_card_option';
import { core_apimessages_CreditCard } from '../../schemas/rql';
import { RCButton } from '@reverbdotcom/cadence/components';
import { MoneyInput } from '@reverbdotcom/commons/src/components/money_input';
import SiteModulePlaceholder from '@reverbdotcom/commons/src/components/site_module_placeholder';
import { formatAmountCents, parseAmountCents } from '@reverbdotcom/commons/src/money';
import { CREDIT_CARD_LIMIT } from '../../lib/creditCards';
import CannotAddNewCardWarning from '../CannotAddNewCardWarning';

export interface StatementProps {
  balanceCents: number;
  currencyCode: string;
  statementId: number;
  totalOwedCents: number;
  totalPaidCents: number;
}

interface IProps extends StatementProps {
  areCardsLoading: boolean;
  closeModal(): void;
  creditCards: core_apimessages_CreditCard[];
  newCardId: string;
  primaryBillingCardId: string;
  toggleDisplayNewCardForm(): void;
}

function PayStatementForm({
  areCardsLoading,
  balanceCents,
  closeModal,
  creditCards,
  currencyCode,
  newCardId,
  primaryBillingCardId,
  toggleDisplayNewCardForm,
  totalOwedCents,
  totalPaidCents,
}: IProps) {
  const [selectedCardId, setSelectedCardId] = useState(null);

  const onSelectCard = useCallback((event) => {
    setSelectedCardId(event.target.value);
  }, [ setSelectedCardId ]);

  const isSubmitDisabled = useMemo(() => {
    if (!selectedCardId) {
      return true;
    }
    return creditCards.find(cc => cc.id === selectedCardId)?.needsReverification;
  }, [ selectedCardId, creditCards ]);

  // set selected card after credit cards finish loading
  useEffect(() => {
    if (!areCardsLoading) {
      setSelectedCardId(newCardId || primaryBillingCardId);
    }
  }, [ areCardsLoading, newCardId ]);

  const totalOwedAmount = useMemo(() => {
    return formatAmountCents({ amountCents: totalOwedCents, currency: currencyCode });
  }, [ totalOwedCents, currencyCode ]);

  const totalPaidAmount = useMemo(() => {
    return formatAmountCents({ amountCents: totalPaidCents, currency: currencyCode });
  }, [ totalPaidCents, currencyCode ]);

  const balanceAmount = useMemo(() => {
    return formatAmountCents({ amountCents: balanceCents, currency: currencyCode });
  }, [ balanceCents, currencyCode ]);

  const balancePlaceholder = useMemo(() => {
    return parseAmountCents(balanceCents, currencyCode).amount;
  }, [ balanceCents, currencyCode ]);

  return (
    <>
      <div className="padding-3">
        <div className="d-flex fx-justify-between weight-bold">
          <Translate text="discovery.payStatement.paymentForm.totalOwed"/>
          <span>{totalOwedAmount}</span>
        </div>
        <div className="d-flex fx-justify-between weight-bold pt-space pb-1">
          <Translate text="discovery.payStatement.paymentForm.totalPaid"/>
          <span>{totalPaidAmount}</span>
        </div>
        <div className="d-flex fx-justify-between weight-bold pt-1 bdt-1 bd--black">
          <Translate
            text="discovery.payStatement.paymentForm.balance"
            className="tt-uppercase"
          />
          <span>{balanceAmount}</span>
        </div>

        <div className="pt-2">
          { areCardsLoading ? (
            <div className="pb-2">
              <SiteModulePlaceholder numPlaceholderLines={2} />
              <SiteModulePlaceholder numPlaceholderLines={2} />
            </div>
          ) : (
            creditCards.map((creditCard) => (
              <CreditCardOption
                key={`credit_card_${creditCard.id}`}
                creditCard={creditCard}
                onSelectCard={onSelectCard}
                isSelected={selectedCardId === creditCard.id}
              />
            ))
          )}
        </div>

        {creditCards.length < CREDIT_CARD_LIMIT ? (
          <RCButton
            fullWidth
            onClick={toggleDisplayNewCardForm}
          >
            <Translate text="discovery.payStatement.paymentForm.addNewCard" />
          </RCButton>
        ) : (
          <CannotAddNewCardWarning />
        )}

        <div className="weight-bold pt-2 size-110">
          <Translate text="discovery.payStatement.paymentForm.howMuchToPay" />
        </div>

        <div className="g-container pt-1">
          <div className="g-col-5">
            <MoneyInput
              type="text"
              placeholder={balancePlaceholder}
              label={I18n.t('discovery.payStatement.paymentForm.paymentAmount')}
              required
              currencyCode={currencyCode}
              suffixText={currencyCode}
            />
          </div>
        </div>
      </div>
      <div className='padding-3 bdt-2 bd--offwhite d-flex fx-justify-end'>
        <RCButton
          onClick={closeModal}
          variant='transparent'
        >
          <Translate text='discovery.payStatement.paymentForm.cancel' />
        </RCButton>
        <div className="pl-3">
          <RCButton
            variant='primary'
            disabled={isSubmitDisabled}
          >
            <Translate text='discovery.payStatement.paymentForm.confirmPayment' />
          </RCButton>
        </div>
      </div>
    </>
  );
}

export default PayStatementForm;
